<template>
    <div class="input">
        <label v-if="label">{{ label }} :</label>
        <div class="input">
            <div class="country">
                <select :value="country" @change="onCountry">
                    <option v-for="country in countries" :key="country.isoCode" :value="country.isoCode">
                        {{ country.name }} ({{ country.phonecode }}) {{ country.flag }}
                    </option>
                </select>
                <div class="flag">
                    <div v-if="country">
                        {{ country.flag }}
                    </div>
                    <InternationalPhoneIcon v-else class="icon"/>
                    <ChevronDown class="chevron"/>
                </div>
            </div>
            <input :value="input" @input="onInput"/>
        </div>
        <small v-if="error">{{ error }}</small>
    </div>
</template>
<script>
import { Country } from 'country-state-city';
import { isSupportedCountry, parsePhoneNumberWithError } from 'libphonenumber-js';

import { ChevronDown, InternationalPhoneIcon } from '../svg';
import { SolutionConsumer } from '../../mixins';

export default {
    name: 'InputPhone',
    components: { ChevronDown, InternationalPhoneIcon },
    mixins: [SolutionConsumer],
    inheritAttrs: false,
    props: {
        value: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: '',
        },
    },
    data () {
        return {
            input: '',
            country: null,
            error: null,
        };
    },
    computed: {
        countries () {
            return Country.getAllCountries().filter(({ isoCode }) => isSupportedCountry(isoCode));
        },
    },
    watch: {
        /**
         * Incoming phone number is in international format, we parse
         * it to deduce country and national number that we manipulate
         * to also emit an international number
         */
        value: {
            immediate: true,
            handler (value) {
                try {
                    this.error = null;
                    const parsed = parsePhoneNumberWithError(value);
                    if (parsed.country) {
                        this.country = Country.getCountryByCode(parsed.country);
                    }
                    this.input = parsed.formatNational();
                } catch (err) {
                    this.error = this.config[`ERROR_PHONE_${err.message}`];
                }
            },
        },
    },
    methods: {
        onUpdate () {
            try {
                this.error = null;
                const { number } = parsePhoneNumberWithError(this.input, this.country.isoCode);
                this.$emit('input', number);
            } catch (err) {
                this.error = this.config[`ERROR_PHONE_${err.message}`];
            }
        },
        onInput (e) {
            this.input = e.target.value;
            this.onUpdate();
        },
        onCountry (e) {
            this.country = Country.getCountryByCode(e.target.value);
            this.onUpdate();
        },
    },
};
</script>
<style lang="scss" scoped>
@import '../../assets/scss/colors';

.input {
    > label {
        display: block;
        padding: 8px 0;
        font-size: 12px;
        font-weight: 400;
        text-align: left;
        color: $black;
        user-select: none;
    }

    > .input {
        display: flex;

        > .country {
            width: 60px;
            position: relative;
            border: 1px solid $grey-lightest;
            border-right: 0;
            border-top-left-radius: 5px;
            border-bottom-left-radius: 5px;
            background-color: $white-darkest;

            > select {
                position: absolute;
                max-width: 100%;
                height: 100%;
                opacity: 0;
            }

            > .flag {
                display: flex;
                justify-content: center;
                align-items: center;
                width: 100%;
                height: 100%;

                .chevron {
                    width: 15px;
                }

                .icon {
                    width: 18px;
                }
            }
        }

        > input {
            display: block;
            flex: 1;
            padding: 16px;
            outline: none;
            border: 1px solid $grey-lightest;
            border-top-right-radius: 4px;
            border-bottom-right-radius: 4px;
            background-color: $white-darkest;
            font-family: inherit;
            font-size: 14px;
            caret-color: var(--COLOR_PRIMARY);
            transition: all .1s ease-in-out;

            &:focus {
                border-color: var(--COLOR_PRIMARY);
                color: $black;
            }

            &:disabled {
                pointer-events: none;
            }

            &::placeholder {
                color: $grey-light;
            }
        }
    }

    > small {
        display: block;
        text-align: right;
        color: $red;
    }
}
</style>
